// GivingPanel.jsx
// Collapsible giving summary for MainView.
// Loads on first expand -- zero cost when collapsed.
// Shows: YTD totals strip only.
// Path: src/components/features/GivingPanel.jsx

import React, { useState, useCallback, useEffect } from "react";
// import React, { useState, useCallback } from "react";
import {
  Gift,
  ChevronDown,
  TrendingUp,
  Users,
  AlertCircle,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { formatCurrency } from "../../constants/giftTypes";
import { getFiscalDates } from "../../utils/fiscalYear";
import { supabase } from "../../lib/supabase";
import { useApp } from "../../context/AppContext";

export const GivingPanel = () => {
  const { palette, chapterSettings } = useApp();

  const bounds = (() => {
    const fy = getFiscalDates(chapterSettings?.fiscalYearStartMonth || 1);
    return { start: fy.yearStart, end: fy.yearEnd, label: fy.label };
  })();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem("cmm_giving_panel") !== "open";
    } catch (e) {
      return true;
    }
  });
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ytd, setYtd] = useState({ total: 0, donorCount: 0, giftCount: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Cursor-paginated fetch to avoid Supabase 1000-row limit
      let ytdRows = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, member_id, amount, gift_type")
          .gte("gift_date", bounds.start)
          .lte("gift_date", bounds.end)
          .not("amount", "is", null)
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);

        const { data, error: ytdErr } = await query;
        if (ytdErr) throw ytdErr;
        if (!data || data.length === 0) break;
        ytdRows = ytdRows.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }

      const total = ytdRows.reduce(function (s, g) {
        return s + (g.amount || 0);
      }, 0);
      const donorCount = new Set(
        ytdRows.map(function (g) {
          return g.member_id;
        })
      ).size;
      const giftCount = ytdRows.length;
      setYtd({ total: total, donorCount: donorCount, giftCount: giftCount });
      setHasLoaded(true);
    } catch (err) {
      setError("Could not load giving data");
    } finally {
      setLoading(false);
    }
  }, []);

  // NEW — fetch on mount if panel was left open
  useEffect(() => {
    if (!collapsed && !hasLoaded) {
      loadData();
    }
  }, []);

  const handleToggle = function () {
    var opening = collapsed;
    setCollapsed(!opening);
    try {
      sessionStorage.setItem("cmm_giving_panel", opening ? "open" : "closed");
    } catch (e) {}
    if (opening && !hasLoaded) {
      loadData();
    }
  };

  const hoverIn = function (e) {
    e.currentTarget.style.background = palette.accent1 + "14";
  };
  const hoverOut = function (e) {
    e.currentTarget.style.background = "transparent";
  };

  return (
    <div
      style={{
        borderBottom: "1px solid " + palette.cardBorder,
        background: palette.toolbarBg,
      }}
    >
      {/* Toggle Header */}
      <div
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 20px",
          cursor: "pointer",
          userSelect: "none",
          transition: "background 0.15s",
        }}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <Gift size={ICON_SIZES.sm} style={{ color: palette.accent1, flexShrink: 0 }} />
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            flex: 1,
            fontFamily: FONT_FAMILY,
          }}
        >
          Giving Activity -- {bounds.label} Fiscal YTD
        </span>

        {/* Collapsed summary pill */}
        {collapsed && ytd.total > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: palette.accent1,
              background: palette.accent1 + "18",
              padding: "2px 10px",
              borderRadius: 999,
              border: "1px solid " + palette.accent1 + "30",
            }}
          >
            {formatCurrency(ytd.total)} -- {ytd.donorCount} donors
          </span>
        )}

        <ChevronDown
          size={ICON_SIZES.sm}
          style={{
            color: palette.text3,
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      </div>

      {/* Expanded Content */}
      {!collapsed && (
        <div style={{ padding: "0 20px 14px" }}>
          {loading && (
            <div
              style={{
                padding: "12px 0",
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
              }}
            >
              Loading...
            </div>
          )}

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 0",
                fontSize: FONT_SIZES.xs,
                color: "#991b1b",
              }}
            >
              <AlertCircle size={ICON_SIZES.sm} />
              {error}
            </div>
          )}

          {!loading && !error && (
            <div style={{ display: "flex", gap: 12 }}>
              {[
                {
                  icon: TrendingUp,
                  label: "Total Raised",
                  value: formatCurrency(ytd.total),
                  highlight: true,
                },
                {
                  icon: Users,
                  label: "Donors",
                  value: ytd.donorCount,
                  highlight: false,
                },
                {
                  icon: Gift,
                  label: "Gifts",
                  value: ytd.giftCount,
                  highlight: false,
                },
              ].map(function (stat) {
                return (
                  <div
                    key={stat.label}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: palette.accent1 + "18",
                      border: "1px solid " + (palette.accent1 + "30"),
                    }}
                  >
                    <stat.icon
                      size={ICON_SIZES.sm}
                      style={{
                        color: stat.highlight ? palette.accent1 : palette.text3,
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.xs,
                          fontWeight: 600,
                          color: palette.text2,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                          lineHeight: 1,
                          marginBottom: 3,
                        }}
                      >
                        {stat.label}
                      </div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.md,
                          fontWeight: 800,
                          color: stat.highlight
                            ? palette.accent1
                            : palette.text1,
                          fontFamily: FONT_FAMILY,
                          lineHeight: 1,
                        }}
                      >
                        {stat.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
